import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { IDeliveriesState } from '../deliveries-state';
import { DispatchDataService } from '@core/data/api/dispatch/dispatch-data.service';
import { mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { FetchDispatchConfigAction, SetDispatchConfigAction, UpdateDispatchConfigAction } from './dispatch-config.actions';
import { DISPATCH_CONFIG_FETCH, DISPATCH_CONFIG_UPDATE, FILTER_SET, PREFERENCE_LOCATION_SET, ROUTER_NAVIGATION } from '@core/state/action-types';
import { DispatchConfig } from '@core/models/delivery/dispatch/dispatch-config';
import { $log } from '@core/utils/logger';
import { schema } from 'normalizr';
import { DISPATCH_CONFIG_ENTITY } from '@core/state/entity-types';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { from } from 'rxjs';
import { getUser } from '@core/state/session/session-selectors';
import { User } from '@core/models/user/user';
import { getDispatchConfigEntities } from '@core/state/shared/entities/entities-selectors';
import { getPreference } from '@core/state/preference/preference-selectors';
import { getDeliveryOrdersFilterBy } from '../deliveries-selectors';
import { getRouterState } from '@core/state/router/router-selectors';
import { DELIVERIES_LIST, DELIVERIES_SETTINGS } from '@core/router/app-states';
import { NoAction } from '@core/state/shared/actions/no-action';
import { SetDispatchConfigFetchingAction } from '../deliveries-actions';

@Injectable()
export class DispatchConfigEffects {
    constructor(
        private actions$: Actions,
        private store$: Store<IDeliveriesState>,
        private dispatchDataService: DispatchDataService
    ) {}

    updateDispatchConfig$ = createEffect(() => this.actions$.pipe(
        ofType<UpdateDispatchConfigAction>(DISPATCH_CONFIG_UPDATE),
        switchMap((action: UpdateDispatchConfigAction) => {
            const { locationId, request } = action.payload;
            const dto = request.export();

            return this.dispatchDataService
                .updateDispatchConfig(locationId, dto)
                .pipe(
                    mergeMap((res: any) => {
                        let actions: Action[] = [];

                        $log.debug('Save dispatch config', res);
                        if (res) {
                            // Inject dispatch config id
                            res.id = locationId;
                            const dispatchConfig: any = DispatchConfig.import(
                                res
                            );
                            actions.push(
                                new SetDispatchConfigAction({
                                    item: dispatchConfig,
                                    schema: new schema.Entity(
                                        DISPATCH_CONFIG_ENTITY
                                    ),
                                })
                            );
                        }
                        actions = [...actions, (action as any).success()];
                        return actions;
                    }),
                    ossCatchError(([actions, error]) =>
                        from(actions.concat([(action as any).error(error)]))
                    )
                );
        })
    ));

    fetchDispatchConfig$ = createEffect(() => this.actions$.pipe(
        ofType<FetchDispatchConfigAction>(DISPATCH_CONFIG_FETCH),
        withLatestFrom(
            this.store$.select(getDispatchConfigEntities),
            this.store$.select(getUser)
        ),
        switchMap(
            ([action, dispatchConfigs, user]: [
            FetchDispatchConfigAction,
                any,
                User
            ]) => {
                const locationId: string = action.payload.locationId;
                return this.dispatchDataService
                    .fetchDispatchConfig(locationId)
                    .pipe(
                        mergeMap((res: any) => {
                            return [
                                new SetDispatchConfigAction({
                                    item: DispatchConfig.import({
                                        ...res,
                                        id: locationId,
                                    }),
                                    schema: new schema.Entity(
                                        DISPATCH_CONFIG_ENTITY
                                    ),
                                }),
                                new SetDispatchConfigFetchingAction({ value: false })
                            ];
                        }),
                        ossCatchError((error) => {
                            return from([
                                new SetDispatchConfigFetchingAction({ value: false }),
                                (action as any).error(error)
                            ]);
                        })
                    );
            }
        )
    ));

    routerDispatchConfigs$ = createEffect(() => this.actions$.pipe(
        ofType(ROUTER_NAVIGATION, PREFERENCE_LOCATION_SET, FILTER_SET),
        withLatestFrom(
            this.store$.select(getPreference),
            this.store$.select(getDeliveryOrdersFilterBy),
            this.store$.select(getRouterState)
        ),
        mergeMap(([action, sessionPreference, filterBy, routerState]) => {
            const { name: stateName } = routerState;
            let actions: Action[] = [];

            if (
                (stateName === DELIVERIES_SETTINGS || stateName === DELIVERIES_LIST) &&
                filterBy?.locations?.length === 1
            ) {
                const locationId: string = filterBy.locations[0];
                actions.push(new FetchDispatchConfigAction({ locationId }));
            } else {
                actions.push(new NoAction());
            }

            return actions;
        })
    ));
}
